import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Select,
    Space,
    Tag,
    Empty,
    Row,
    Col,
    Statistic
} from 'antd';
import {
    BankOutlined,
    HomeOutlined,
    CalendarOutlined,
    TrophyOutlined
} from '@ant-design/icons';
import axiosInstance from '../../../../variables/api/axiosInstance';
import { displayNumbers } from '../../../../variables/numbersFunctions';

const TableauxRegroupes = ({ exercice, organismeCode, agenceCode, categorieCode }) => {
    const [typeRegroupement, setTypeRegroupement] = useState('agence');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);

    useEffect(() => {
        if (exercice) {
            chargerDonneesRegroupees();
        }
    }, [exercice, typeRegroupement, organismeCode, agenceCode, categorieCode]);

    const chargerDonneesRegroupees = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/api/stats/production/regroupees', {
                params: {
                    exercice,
                    regroupement: typeRegroupement,
                    organismeCode,
                    agenceCode,
                    categorieCode
                }
            });
            setData(response.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: typeRegroupement === 'agence' ? 'Agence' :
                typeRegroupement === 'organisme' ? 'Organisme' : 'Mois',
            dataIndex: 'groupeNom',
            key: 'groupeNom',
            fixed: 'left',
            width: 180,
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: 600, fontSize: '14px' }}>
                        {text}
                    </div>
                    {record.sousGroupe && (
                        <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                            {record.sousGroupe}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Contrats',
            dataIndex: 'nombreContrats',
            key: 'nombreContrats',
            width: 100,
            align: 'center',
            sorter: (a, b) => a.nombreContrats - b.nombreContrats,
            render: (val) => <Tag color="blue">{val}</Tag>
        },
        {
            title: 'Émissions',
            children: [
                {
                    title: 'Nombre',
                    dataIndex: 'nombreEmissions',
                    key: 'nombreEmissions',
                    width: 90,
                    align: 'center',
                    render: (val) => <Tag color="green">{val}</Tag>
                },
                {
                    title: 'Montant',
                    dataIndex: 'montantEmissions',
                    key: 'montantEmissions',
                    width: 140,
                    align: 'right',
                    sorter: (a, b) => a.montantEmissions - b.montantEmissions,
                    render: (val) => (
                        <span style={{ color: '#52c41a', fontWeight: 500 }}>
                            {displayNumbers(val)} F
                        </span>
                    )
                }
            ]
        },
        {
            title: 'Annulations',
            children: [
                {
                    title: 'Nombre',
                    dataIndex: 'nombreAnnulations',
                    key: 'nombreAnnulations',
                    width: 90,
                    align: 'center',
                    render: (val) => <Tag color="red">{val}</Tag>
                },
                {
                    title: 'Montant',
                    dataIndex: 'montantAnnulations',
                    key: 'montantAnnulations',
                    width: 140,
                    align: 'right',
                    sorter: (a, b) => a.montantAnnulations - b.montantAnnulations,
                    render: (val) => (
                        <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                            {displayNumbers(val)} F
                        </span>
                    )
                }
            ]
        },
        {
            title: 'Production Nette',
            dataIndex: 'montantNet',
            key: 'montantNet',
            width: 160,
            align: 'right',
            fixed: 'right',
            sorter: (a, b) => a.montantNet - b.montantNet,
            defaultSortOrder: 'descend',
            render: (val) => (
                <span style={{
                    color: val >= 0 ? '#1890ff' : '#ff4d4f',
                    fontWeight: 700,
                    fontSize: '14px'
                }}>
                    {displayNumbers(val)} F
                </span>
            )
        }
    ];

    // Calculer les totaux et le top 3
    const calculerStatistiques = () => {
        if (data.length === 0) return null;

        const total = data.reduce((acc, item) => ({
            montantNet: acc.montantNet + (item.montantNet || 0),
            montantEmissions: acc.montantEmissions + (item.montantEmissions || 0),
            montantAnnulations: acc.montantAnnulations + (item.montantAnnulations || 0)
        }), { montantNet: 0, montantEmissions: 0, montantAnnulations: 0 });

        const top3 = [...data]
            .sort((a, b) => b.montantNet - a.montantNet)
            .slice(0, 3);

        return { total, top3 };
    };

    const stats = calculerStatistiques();

    return (
        <div>
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={16} align="middle">
                    <Col xs={24} sm={12} md={8}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <label style={{ fontWeight: 500 }}>Type de regroupement</label>
                            <Select
                                value={typeRegroupement}
                                onChange={setTypeRegroupement}
                                style={{ width: '100%' }}
                                options={[
                                    {
                                        value: 'agence',
                                        label: (
                                            <span>
                                                <HomeOutlined /> Par agence
                                            </span>
                                        )
                                    },
                                    {
                                        value: 'organisme',
                                        label: (
                                            <span>
                                                <BankOutlined /> Par organisme
                                            </span>
                                        )
                                    },
                                    {
                                        value: 'mois',
                                        label: (
                                            <span>
                                                <CalendarOutlined /> Par mois
                                            </span>
                                        )
                                    }
                                ]}
                            />
                        </Space>
                    </Col>

                    {stats && (
                        <>
                            <Col xs={24} sm={12} md={8}>
                                <Statistic
                                    title="Production totale"
                                    value={displayNumbers(stats.total.montantNet)}
                                    suffix="F"
                                    valueStyle={{ color: '#1890ff', fontSize: '18px' }}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <Statistic
                                    title={`Meilleur${typeRegroupement === 'mois' ? ' mois' :
                                        typeRegroupement === 'agence' ? 'e agence' : ' organisme'}`}
                                    value={stats.top3[0]?.groupeNom || 'N/A'}
                                    prefix={<TrophyOutlined style={{ color: '#faad14' }} />}
                                    valueStyle={{ fontSize: '14px', color: '#262626' }}
                                />
                            </Col>
                        </>
                    )}
                </Row>
            </Card>

            {stats && stats.top3.length > 0 && (
                <Card
                    title={
                        <Space>
                            <TrophyOutlined style={{ color: '#faad14' }} />
                            <span>Top 3 des meilleurs performers</span>
                        </Space>
                    }
                    style={{ marginBottom: 16 }}
                >
                    <Row gutter={16}>
                        {stats.top3.map((item, index) => (
                            <Col key={index} xs={24} sm={8}>
                                <Card
                                    size="small"
                                    style={{
                                        background: index === 0 ? '#fff7e6' :
                                            index === 1 ? '#f0f5ff' : '#f6ffed',
                                        border: `2px solid ${index === 0 ? '#faad14' :
                                            index === 1 ? '#1890ff' : '#52c41a'
                                            }`
                                    }}
                                >
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{
                                            fontSize: '32px',
                                            color: index === 0 ? '#faad14' :
                                                index === 1 ? '#1890ff' : '#52c41a'
                                        }}>
                                            {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                                        </div>
                                        <div style={{
                                            fontWeight: 600,
                                            fontSize: '16px',
                                            marginTop: 8,
                                            marginBottom: 8
                                        }}>
                                            {item.groupeNom}
                                        </div>
                                        <div style={{
                                            fontSize: '20px',
                                            fontWeight: 700,
                                            color: '#1890ff'
                                        }}>
                                            {displayNumbers(item.montantNet)} F
                                        </div>
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#8c8c8c',
                                            marginTop: 4
                                        }}>
                                            {item.nombreContrats} contrat(s)
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Card>
            )}

            <Card>
                <Table
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    rowKey={(record) => `${record.groupeCode}-${record.sousGroupe || 'default'}`}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showTotal: (total) => `${total} ligne(s)`
                    }}
                    scroll={{ x: 1200 }}
                    bordered
                    size="middle"
                    locale={{
                        emptyText: <Empty description="Aucune donnée disponible" />
                    }}
                />
            </Card>
        </div>
    );
};

export default TableauxRegroupes;